import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TextField
} from '@mui/material';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import useFullScreen from '../../hooks/useFullScreen';
import MarkdownWithMermaid from '../common/MarkdownWithMermaid';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import mermaid from 'mermaid';
import SectionHeader from '../atoms/Header';
import TopBarButtons from '../atoms/TopBarButtons';
import LoadMoreButton from '../atoms/LoadMore';
import ConfirmationDialog from '../common/ConfirmationDialog';
import useDevelopmentGuidelinesCRUD from '../../hooks/useDevelopmentGuidelinesCRUD';
import useContentActions from '../../hooks/useContentActions';
import useNotification from '../../hooks/useNotification';
import EmptyContentPlaceholder from '../atoms/EmptyContentPlaceholder';
import Accordion from '../atoms/Accordion';
import { ScrollableBox, EditableTextField } from '../atoms';
import useExplain from '../../hooks/useExplain';
import ExplanationModal from '../atoms/ExplanationModal';
import useImprove from '../../hooks/useImprove';
import InlineImproveInput from '../common/InlineImproveInput';
import ImprovementModal from '../atoms/ImprovementModal';
import useDevelopmentGuidelinesVersions from '../../hooks/useDevelopmentGuidelinesVersions';
import VersionSelector from '../atoms/VersionSelector';
import LoadingBox from '../atoms/LoadingBox';
import AddSectionModal from '../atoms/AddSectionModal';
import conversationService from '../../services/conversationService';
import MarkdownRenderer from '../common/MarkdownRenderer';
import useParsedSections from '../../hooks/useParsedSections';

// Initialize mermaid config
try {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'loose',
    flowchart: { useMaxWidth: false, htmlLabels: true },
    sequence: { diagramMarginX: 50, diagramMarginY: 10 },
    classDiagram: { useMaxWidth: false },
    er: { useMaxWidth: false },
    gantt: { useMaxWidth: false }
  });
} catch (error) {
  console.error('Error initializing mermaid:', error);
}


/**
 * A split-view layout component that shows conversation on the left
 * and dynamically rendered development guidelines on the right with accordion sections
 */
const SplitViewDevelopment = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const { showNotification } = useNotification();
  const developmentPanelRef = useRef(null);

  // Use version management for development guidelines
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useDevelopmentGuidelinesVersions(conversation, isStreaming, streamingContent);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedSections,
    setSections: setParsedSections,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  const [expandedSections, setExpandedSections] = useState({});

  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedSections, setExpandedSections);
  const [visibleSections, setVisibleSections] = useState(10);
  const isLocalUpdateRef = useRef(false); // Track if we're doing a local update

  // Track previous version index to detect actual version changes
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Check if this is a dev guidelines conversation
  const _tid = conversation?.taskId || conversation?.task_id;
  const isDevGuidelinesConversation = _tid === 'dev-guidelines' || _tid === 'development';

  // Version switching: reset visible sections when switching versions
  useEffect(() => {
    if (currentVersion) {
      if (prevVersionIndexRef.current !== currentVersionIndex) {
        setVisibleSections(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  // Section management functions
  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleLoadMore = () => {
    setVisibleSections(prev => Math.min(prev + 10, parsedSections.length));
  };

  const handleShowAll = () => {
    setVisibleSections(parsedSections.length);
  };

  // Alias for compatibility
  const sections = parsedSections;
  const setSections = setParsedSections;
  const hasContent = parsedSections.length > 0;

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use Development Guidelines CRUD hook with category support
  const {
    editingSection,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteSection,
    handleDeleteSectionConfirmed,
    handleCopySection,
    handleAddSection,
    reconstructMarkdown
  } = useDevelopmentGuidelinesCRUD(
    conversation,
    sections,
    setSections,
    setExpandedSections,
    contentRef,
    updateCurrentVersion,
    currentVersion,
    isLocalUpdateRef,
    currentVersionIndex
  );

  // Improve functionality
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Handler for applying improved content
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;
    try {
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }
      closeImprovementModal();
      showNotification({ message: 'Section improved successfully', type: 'success' });
    } catch (error) {
      showNotification({ message: 'Failed to apply improvement', type: 'error' });
    }
  };

  // Custom explain handler that uses the real explain functionality
  const handleExplainSection = (section) => {
    requestExplanation(section);
  };

  // Handler for adding new section - delegates to hook
  const handleAddNewSection = async (newSectionData) => {
    const success = await handleAddSection(newSectionData);
    if (success) {
      setShowAddSectionModal(false);
    }
  };

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport,
    handleCopy,
    handleRefreshDiagrams,
    handleSaveToDocument,
    getExportContent,
  } = useContentActions(contentRef, 'Development Guidelines', projectId, conversation?._id);

  // Full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Initialize validation state for sections with version-specific IDs
  const initialValidationState = sections.reduce((acc, section) => {
    // Create version-specific validation ID
    const versionSpecificId = `${section.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook with version-specific IDs
  const {
    validatedItems: validatedSections,
    handleValidateItem: handleValidateSectionBase,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'dev-guidelines',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'dev-guidelines',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateSection = async (sectionId) => {
    // Create version-specific ID
    const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateSectionBase(versionSpecificId);
  };

  console.log('SplitViewDevelopment rendering', {
    conversationId: conversation?._id,
    taskId: conversation?.taskId,
    isDevGuidelinesConversation,
    isStreaming
  });


  // Auto-scroll panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && developmentPanelRef.current && isDevGuidelinesConversation) {
      setTimeout(() => {
        if (developmentPanelRef.current) {
          developmentPanelRef.current.scrollTop = developmentPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [isStreaming, isDevGuidelinesConversation]);

  // Don't show the split view if this isn't a development guidelines conversation or if there's no content

  if (!isDevGuidelinesConversation || (!hasContent && !isStreaming)) {
    console.log('Not showing split view: either not development guidelines conversation or no content available');
    return <>{children}</>;
  }

  console.log('Showing split view with development guidelines content', { hasContent, isStreaming });

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      mt: 5
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(sidebarCollapsed),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Development guidelines panel */}
        <Box
          ref={developmentPanelRef}
          sx={{
            ...getRightPanelStyles(sidebarCollapsed),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}>
                <SectionHeader label='Development guidelines' />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {hasVersions && versions.length > 1 && (
                    <VersionSelector
                      versions={versions}
                      currentVersionIndex={currentVersionIndex}
                      onVersionChange={setCurrentVersionIndex}
                      disabled={isStreaming}
                    />
                  )}
                  {/* Fixed header section */}
                  <TopBarButtons
                    isFullScreen={isFullScreen}
                    onToggleFullScreen={toggleFullScreen}
                    onAdd={() => setShowAddSectionModal(true)}
                    getExportContent={getExportContent}
                  onExport={handleExport}
                    isExporting={isExporting}
                    onCopy={handleCopy}
                    onRefresh={handleRefreshDiagrams}
                    onSave={projectId ? () => handleSaveToDocument('DEV_GUIDELINES') : undefined}
                    isSaving={isSaving || isSavingDocument}
                    disabled={!contentRef.current}
                    addTooltip="Add new section"
                    exportTooltip="Export development guidelines as markdown"
                    copyTooltip="Copy development guidelines to clipboard"
                    refreshTooltip="Refresh diagrams"
                    saveTooltip="Save as document"
                  />
                </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'dev-guidelines'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedSections}
              totalItems={parsedSections?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              position: 'relative',
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>
              {/* Show development guidelines content or a placeholder if none found yet */}
              {isStreaming ? (
                <LoadingBox message='Generating development guidelines...' spinnerSize={40} />
              ) : sections.length > 0 ? (
                <Box>
                  {/* Render sections as accordions grouped by category */}
                  {(() => {
                    const groups = groupedSections;
                    let globalIndex = 0;
                    return groups.slice(0, visibleSections).map((group, groupIndex) => (
                      <Box key={group.categoryId} sx={{ mb: 3 }}>
                        {group.category && (
                          <Box sx={{ mt: groupIndex === 0 ? 0 : 2, mb: 1.5 }}>
                            <SectionHeader label={group.category} />
                          </Box>
                        )}

                        {group.categorySection && (
                          <Box sx={{ pl: 2, mb: 2 }}>
                            <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                              <MarkdownRenderer content={group.categorySection.content || ''} />
                            </Box>
                          </Box>
                        )}

                        <Box sx={{ pl: group.category ? 2 : 0 }}>
                          {group.sections.map((section) => {
                            const idx = globalIndex++;
                            const sectionId = section.id;
                            const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                            const isValidated = validatedSections[versionSpecificId] || false;

                            return (
                            <React.Fragment key={section.id}>
                            <Accordion
                              ref={shouldHighlight(section.id) ? highlightRef : null}
                              key={section.id}
                              title={`${idx + 1}. ${section.title}`}
                              defaultExpanded={false}
                              expanded={expandedSections[section.id]}
                              onChange={() => handleSectionToggle(section.id)}
                              titleContent={editingSection === section.id ? (
                                <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                                  <TextField
                                    value={editedTitle}
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.target.removeAttribute('title')}
                                    size="small"
                                    fullWidth
                                    placeholder="Enter guideline title"
                                    variant="outlined"
                                    InputProps={{ title: '' }}
                                    inputProps={{ title: '', autoComplete: 'off' }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        backgroundColor: '#fff',
                                        '& fieldset': { borderWidth: '1px' },
                                        '&:hover fieldset': { borderWidth: '1px' },
                                        '&.Mui-focused fieldset': { borderWidth: '1px' },
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        padding: '8px 12px',
                                      },
                                    }}
                                  />
                                </Box>
                              ) : null}
                              rightIcon={
                                <Box
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    try {
                                      await handleValidateSection(sectionId);
                                    } catch (error) {
                                      showNotification({
                                        message: `Failed to update validation for "${section.title}"`,
                                        type: "error"
                                      });
                                    }
                                  }}
                                  sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                                >
                                  {isValidated ? (
                                    <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                                  ) : (
                                    <UnvalidatedIcon size={25} />
                                  )}
                                </Box>
                              }
                            >
                              <ScrollableBox
                                minHeight="220px"
                                sx={{
                                  textAlign: 'justify',
                                  cursor: editingSection === section.id ? 'default' : 'pointer',
                                }}
                                onClick={() => {
                                  if (!editingSection && editingSection !== section.id) {
                                    handleStartEdit(section.id, section.content || '', section.title || '');
                                  }
                                }}
                              >
                                {editingSection === section.id ? (
                                  <EditableTextField
                                    value={editedContent}
                                    onChange={(e) => setEditedContent(e.target.value)}
                                  />
                                ) : (
                                  <Box
                            ref={getSectionContentRef(section.id)}
                            sx={{ position: "relative" }}
                          >
                            <MarkdownWithMermaid content={section.content} />
                            <TextSelectionPopup
                                    containerRef={getSectionContentRef(section.id)}
                                    sectionId={section.id}
                                    onSubmitComment={addComment}
                                    disabled={editingSection === section.id}

                                  />
                          </Box>
                                )}
                              </ScrollableBox>

                              <Box sx={{
                                position: 'absolute',
                                right: 10,
                                top: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5
                              }}>
                                <ActionButtons
                                  item={section}
                                  itemIndex={idx}
                                  featureId={section.id}
                                  editingFeature={editingSection}
                                  isSaving={isSaving}
                                  onEdit={() => handleStartEdit(section.id, section.content || '', section.title || '')}
                                  onSave={() => handleSaveEdit(section.id)}
                                  onCancel={handleCancelEdit}
                                  onImprove={() => handleImproveClick(section)}
                                  onExplain={() => handleExplainSection(section)}
                                  onCopy={() => handleCopySection(section)}
                                  onDelete={() => handleDeleteSection(section)}
                                  vertical={true}
                          onComment={() => openCommentPanel(section.id)}
                          commentCount={commentCounts[section.id]?.open || 0}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                                />
                              </Box>
                            </Accordion>
                            {showImproveInput === section.id && (
                              <InlineImproveInput
                                improvePrompt={improvePrompt}
                                onPromptChange={setImprovePrompt}
                                onSend={() => handleSendImprove(section)}
                                onCancel={handleCancelImprove}
                              />
                            )}
                            </React.Fragment>
                            );
                          })}
                        </Box>
                      </Box>
                    ));
                  })()}

                  {/* Load more button for lazy loading */}
                  {(() => {
                    const groups = groupedSections;
                    return groups.length > visibleSections && (
                      <LoadMoreButton
                        remaining={groups.length - visibleSections}
                        onLoadMore={handleLoadMore}
                        onShowAll={handleShowAll}
                        label="Load more sections"
                      />
                    );
                  })()}
                </Box>
              ) : (
                <EmptyContentPlaceholder
                  title="No development guidelines found yet."
                  description="Ask the AI to create comprehensive development guidelines based on the technology stack and architecture. You can use prompts like:"
                  examplePrompt="Based on our technology stack, please create development guidelines covering code organization, coding standards, design patterns, and SOLID principles."
                  additionalInfo="You can also ask for specific guidelines about testing strategies, performance optimization, or security best practices."
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Confirmation dialog for delete */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        onConfirm={handleDeleteSectionConfirmed}
        title="Delete Section"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height='auto'
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Add Section Modal */}
      <AddSectionModal
        open={showAddSectionModal}
        onClose={() => setShowAddSectionModal(false)}
        onSave={handleAddNewSection}
        sectionType="guideline"
        existingSections={sections}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        onUpdate={handleUpdateImprovedContent}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedSections || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewDevelopment;
